import type { ReactNode } from 'react';
import { useScope } from '../lib/scope';
import { Button, EmptyState } from './ui';

export interface ScopedEmptyStateProps {
  title: string;
  description?: string;
  /** Der Aufrufer meldet, ob Seitenfilter (Suche, Kategorie, Schalter) aktiv sind. */
  filtersActive?: boolean;
  /** Setzt die Seitenfilter zurueck. Ohne Callback gibt es keinen Knopf. */
  onResetFilters?: () => void;
  /** Anlegen-Aktion; erscheint nur, wenn weder Bereich noch Filter aktiv sind. */
  createAction?: ReactNode;
}

/**
 * Leerzustand fuer Listen, die nach dem gewaehlten Bereich filtern.
 *
 * Zeigt, dass der Bereich die Liste verengt, und bietet einen Weg zurueck -
 * sonst wirkt eine leere Liste wie ein Fehler oder wie fehlende Daten.
 */
export function ScopedEmptyState({
  title,
  description,
  filtersActive = false,
  onResetFilters,
  createAction,
}: ScopedEmptyStateProps): JSX.Element {
  const scope = useScope();
  const unitId = scope.unitId;
  const scoped = unitId !== null;
  const narrowed = scoped || filtersActive;

  const note =
    unitId !== null
      ? `Gefiltert auf ‚${scope.unit?.name ?? scope.unitName(unitId)}‘${
          scope.includeChildren ? ' und untergeordnete Bereiche' : ''
        }`
      : undefined;

  const showReset = filtersActive && Boolean(onResetFilters);
  const hasActions = scoped || showReset;

  return (
    <EmptyState
      title={title}
      description={description}
      note={note}
      action={narrowed ? undefined : createAction}
      actions={
        hasActions ? (
          <>
            {scoped && (
              <Button size="sm" variant="secondary" onClick={() => scope.setUnitId(null)}>
                Gesamtverein anzeigen
              </Button>
            )}
            {showReset && (
              <Button size="sm" variant="secondary" onClick={onResetFilters}>
                Filter zurücksetzen
              </Button>
            )}
          </>
        ) : undefined
      }
    />
  );
}
